import { Spinner } from "@medusajs/icons"
import { useEffect, useState } from "react"
import { Link, Navigate, Outlet, useLocation } from "react-router-dom"
import { useLogout } from "../../../hooks/api"
import { useMe } from "../../../hooks/api/users"
import { useAdminRole } from "../../../hooks/api/auth-role"
import { queryClient } from "../../../lib/query-client"
import { SearchProvider } from "../../../providers/search-provider"
import { SidebarProvider } from "../../../providers/sidebar-provider"

export const ProtectedRoute = () => {
  const { user, isLoading } = useMe()
  const { data: roleData, isLoading: isRoleLoading } = useAdminRole({
    enabled: !!user,
  })
  const location = useLocation()
  const { mutateAsync: logout } = useLogout()
  const [isForbidden, setIsForbidden] = useState(false)

  const allowList = [
    "/merchants",
    "/merchant-business-types",
    "/verification",
    "/product-approvals",
    "/products",
    "/reservations",
    "/inventory",
    "/categories",
    "/regions",
    "/tax-regions",
    "/store",
    "/locations",
    "/homepage-settings",
    "/catalog-translation",
    "/operations-docs",
    "/settings",
    "/login",
    "/reset-password",
    "/invite",
  ]

  const isAllowedPath = allowList.some((prefix) => {
    return (
      location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
    )
  })

  useEffect(() => {
    if (!user || isRoleLoading || !roleData?.role) {
      return
    }
    if (roleData.role !== "super_admin") {
      setIsForbidden(true)
      logout().finally(() => queryClient.clear())
    }
  }, [user, isRoleLoading, roleData?.role, logout])

  if (isLoading || isRoleLoading || (user && !roleData?.role)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="text-ui-fg-interactive animate-spin" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  const isKnownNonSuperAdmin = !!user && roleData?.role !== "super_admin"

  if (isForbidden || isKnownNonSuperAdmin) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3">
        <div className="text-ui-fg-base text-xl font-semibold">Forbidden</div>
        <div className="text-ui-fg-subtle text-sm">
          Your account does not have access to this portal.
        </div>
        <Link to="/login" className="text-ui-fg-interactive text-sm">
          Back to login
        </Link>
      </div>
    )
  }

  if (!isAllowedPath) {
    return <Navigate to="/merchants" replace />
  }

  return (
    <SidebarProvider>
      <SearchProvider>
        <Outlet />
      </SearchProvider>
    </SidebarProvider>
  )
}
